import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { claimGoogleCredentialHandler, initGoogleId } from '../auth/gis'
import {
  isNativeGoogleSignInAvailable,
  signInWithNativeGoogle,
} from '../auth/nativeAuth'
import { useAuth } from '../auth/useAuth'

type LocationState = { from?: { pathname: string } } | null

export default function Login() {
  const { state, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [nativeBusy, setNativeBusy] = useState(false)
  const useNative = isNativeGoogleSignInAvailable()

  const from = (location.state as LocationState)?.from?.pathname ?? '/'

  useEffect(() => {
    if (state.status === 'authenticated') {
      navigate(from, { replace: true })
    }
  }, [state.status, from, navigate])

  const loginRef = useRef(login)
  useEffect(() => {
    loginRef.current = login
  }, [login])

  useEffect(() => {
    if (useNative) return
    let cancelled = false

    // GIS is initialized once, globally (see gis.ts); this page just claims
    // the credential handler for as long as it is mounted.
    const release = claimGoogleCredentialHandler(async (credential) => {
      try {
        setError(null)
        await loginRef.current(credential)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed')
      }
    })

    initGoogleId()
      .then((google) => {
        if (cancelled || !buttonRef.current) return
        google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
          logo_alignment: 'left',
          width: 320,
          type: 'standard',
        })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err))
        }
      })

    return () => {
      cancelled = true
      release()
    }
  }, [useNative])

  const handleNativeSignIn = async () => {
    if (nativeBusy) return
    setNativeBusy(true)
    setError(null)
    try {
      const idToken = await signInWithNativeGoogle()
      await loginRef.current(idToken)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed')
    } finally {
      setNativeBusy(false)
    }
  }

  useEffect(() => {
    if (state.status === 'unauthenticated' && state.error) {
      setError(state.error)
    }
  }, [state])

  return (
    <section className="login-page">
      <div className="login-card">
        <span className="login-page__wordmark">
          <img src="/oroblanco.svg" alt="" width={28} height={28} />
          Oroblanco
        </span>
        <h1 className="login-page__title">
          Sign in
        </h1>
        <p className="login-page__lede">Non-Google sign-in available in the future.</p>
        <hr className="divider-dashed" />
        <div className="login-page__gsi">
          {useNative ? (
            <button
              type="button"
              className="login-page__native-btn"
              onClick={handleNativeSignIn}
              disabled={nativeBusy}
            >
              {nativeBusy ? 'Signing in…' : 'Continue with Google'}
            </button>
          ) : (
            <div ref={buttonRef} />
          )}
        </div>
        {error && (
          <p role="alert" className="login-page__error">
            {error}
          </p>
        )}
      </div>
    </section>
  )
}
